/*!
 * ETAR theme v4.2.4 — Page arch tilt, our story (pour line + counters), contact letter, collection bolts
 * Developed by FACTICIED · https://www.facticied.com
 */
(() => {
  const still = () => document.documentElement.classList.contains('et-no-motion') || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = () => matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Tilt an element toward the pointer
  const tilt = (host, target, max, signal, glare) => {
    if (still() || !fine()) return;
    host.addEventListener('pointermove', (e) => {
      const r = host.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      target.style.setProperty('--ry', `${((x - 0.5) * max).toFixed(2)}deg`);
      target.style.setProperty('--rx', `${((0.5 - y) * max * 0.8).toFixed(2)}deg`);
      if (glare) { target.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`); target.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`); }
    }, { signal });
    host.addEventListener('pointerleave', () => ['--rx', '--ry'].forEach((p) => target.style.removeProperty(p)), { signal });
  };

  if (!customElements.get('et-page-art')) {
    customElements.define('et-page-art', class extends HTMLElement {
      connectedCallback() {
        this.ac = new AbortController();
        const arch = this.querySelector('[data-arch]');
        if (arch) tilt(arch, arch, 14, this.ac.signal);
      }
      disconnectedCallback() { this.ac?.abort(); }
    });
  }

  // About page: the gold line pours down with the scroll, steps light up, numbers count up
  if (!customElements.get('et-story')) {
    customElements.define('et-story', class extends HTMLElement {
      connectedCallback() {
        if (this._on) return; this._on = true;
        this.ac = new AbortController();
        const signal = this.ac.signal;
        const arch = this.querySelector('[data-tilt]');
        if (arch) tilt(arch, arch.querySelector('.et-st__arch-in') || arch, 12, signal);
        this.track = this.querySelector('[data-steps]');
        this.pour = this.querySelector('[data-pour]');
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        if (still() || !this.track) { this.steps.forEach((x) => x.classList.add('is-lit')); if (this.pour) this.pour.style.setProperty('--pour', 1); }
        else {
          const run = () => { this.raf = 0; this.update(); };
          window.addEventListener('scroll', () => { if (!this.raf) this.raf = requestAnimationFrame(run); }, { passive: true, signal });
          window.addEventListener('resize', run, { signal });
          this.update();
        }
        const nums = Array.from(this.querySelectorAll('[data-count]'));
        if (nums.length) {
          this.io = new IntersectionObserver((en) => en.forEach((e) => { if (e.isIntersecting) { this.io.unobserve(e.target); this.count(e.target); } }), { threshold: 0.6 });
          nums.forEach((n) => this.io.observe(n));
        }
      }
      disconnectedCallback() { this.ac?.abort(); this.io?.disconnect(); cancelAnimationFrame(this.raf); }
      update() {
        const r = this.track.getBoundingClientRect(), vh = innerHeight;
        const p = Math.max(0, Math.min(1, (vh * 0.62 - r.top) / r.height));
        this.pour.style.setProperty('--pour', p.toFixed(3));
        const y = r.top + p * r.height;
        this.steps.forEach((st) => { const sr = st.getBoundingClientRect(); st.classList.toggle('is-lit', sr.top + sr.height / 2 <= y + 4); });
      }
      count(el) {
        const raw = el.dataset.count, m = raw.match(/^(\D*)([\d.,]+)(.*)$/);
        if (!m || still()) return;
        const end = parseFloat(m[2].replace(/,/g, '')), dec = (m[2].split('.')[1] || '').length, t0 = performance.now(), dur = 1400;
        const fmt = (v) => m[1] + v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) + m[3];
        const step = (t) => { const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3); el.textContent = fmt(end * e); if (k < 1) requestAnimationFrame(step); };
        requestAnimationFrame(step);
      }
    });
  }

  if (!customElements.get('et-bolts')) {
    customElements.define('et-bolts', class extends HTMLElement {
      connectedCallback() {
        this.ac = new AbortController();
        this.querySelectorAll('[data-tilt]').forEach((b) => tilt(b, b, 12, this.ac.signal, true));
      }
      disconnectedCallback() { this.ac?.abort(); }
    });
  }

  if (!customElements.get('et-contact')) {
    customElements.define('et-contact', class extends HTMLElement {
      connectedCallback() {
        this.ac = new AbortController();
        const letter = this.querySelector('[data-letter]');
        const form = letter && letter.querySelector('form');
        if (form && !still()) {
          form.addEventListener('submit', (e) => {
            if (form.dataset.sealed || !form.checkValidity()) return;
            e.preventDefault();
            form.dataset.sealed = '1';
            letter.classList.add('is-sealing');
            setTimeout(() => HTMLFormElement.prototype.submit.call(form), 750);
          }, { signal: this.ac.signal });
        }
        if (this.querySelector('[data-sent]')) letter?.scrollIntoView({ block: 'center' });
      }
      disconnectedCallback() { this.ac?.abort(); }
    });
  }
})();
